<template>
    <b-modal :id="modalId" centered hide-footer no-close-on-backdrop :body-class="'px-4 pb-4 pt-3'"
        no-close-on-esc @shown="checkCredits">
        <template #modal-header="{ close }">
            <h5 class="modal-title">
                {{ title }}
            </h5>
            <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
                Close Modal
            </b-button>
        </template>
        <div class="d-block">
            <b-row>
                <b-col sm="12">
                    <div class="upload-btn-wrapper mt-2" :class="uploadedFiles.length > 0 ? 'h-auto' : 'h-240px'"
                        @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop"
                        :style="{ border: isDragging ? '2px dashed #007bff' : '' }">
                        <div v-if="uploadedFiles.length === 0" class="text-center">
                            <b-img class="img-2" :src="require('@/assets/drop.svg')"></b-img>
                            <button class="foo-2 w-100">
                                {{ multiple ? $t('importWithAi.dropMulti') : $t('importWithAi.dropSingle') }}
                                <br />
                                <span class="font-12 text-muted">{{ $t('importWithAi.supports') }}</span>
                            </button>
                            <input ref="aiFile" type="file" name="aiFile" :multiple="multiple"
                                accept=".pdf, .png, .jpg, .jpeg" @change="onFileChange" />
                        </div>

                        <div v-else class="files-list-container">
                            <div class="d-flex align-items-center justify-content-between mb-3">
                                <h6 class="mb-0">{{ $t('importWithAi.selectedFiles', { count: uploadedFiles.length }) }}</h6>
                                <b-button v-if="multiple" size="sm" variant="outline-primary"
                                    @click="$refs.aiFile.click()">
                                    {{ $t('importWithAi.addMore') }}
                                </b-button>
                            </div>

                            <div class="files-list">
                                <div v-for="(file, index) in uploadedFiles" :key="index"
                                    class="file-item d-flex align-items-center justify-content-between p-2 mb-2">
                                    <div class="d-flex align-items-center flex-grow-1">
                                        <div class="file-icon mr-3"></div>
                                        <div class="file-info">
                                            <p class="mb-0 font-weight-bold font-14 pre-line w-250px">{{ file.name }}</p>
                                            <p class="mb-0 text-muted font-12">{{ getFileType(file.name) }}</p>
                                        </div>
                                    </div>
                                    <b-img :src="require('@/assets/Close.svg')" class="ai-file-remove"
                                        @click="removeFile(index)" width="20"></b-img>
                                </div>
                            </div>

                            <input ref="aiFile" type="file" name="aiFile" :multiple="multiple"
                                accept=".pdf, .png, .jpg, .jpeg" @change="onFileChange" class="d-none" />
                        </div>

                        <p v-if="uploadErrors && uploadErrors.document" class="field-error mt-2">
                            {{ uploadErrors.document[0] }}
                        </p>
                    </div>
                </b-col>
            </b-row>
        </div>
        <div class="text-right modal-footer border-0 p-0 mt-3">
            <b-button class="mx-0 mr-3 h-40px w-100px" variant="secondary" @click="closeModal">{{ $t('importWithAi.cancel') }}</b-button>
            <b-button class="mx-0 mb-0 h-40px w-100px mt-0" variant="primary" @click="submitFiles">
                {{ $t('importWithAi.save') }}
            </b-button>
        </div>
    </b-modal>
</template>
<script>
import { mapActions } from "vuex";
export default {
    name: "ImportWithAI",
    props: {
        modalId: { type: String, default: 'modal-import-ai' },
        title: { type: String, default: 'Import with AI' },
        multiple: { type: Boolean, default: true },
        maintenanceId: { type: Number, default: null },
    },
    data() {
        return {
            uploadedFiles: [],
            isDragging: false,
            uploadErrors: null,
            availableCredits: null,
        };
    },
    methods: {
        ...mapActions(["extractMaintenanceFromDocument", "updateMaintenanceFromDocument", "setNotification", "setLoader", "getCompany", "getAICredits"]),
        resetFields() {
            this.uploadedFiles = [];
            this.isDragging = false;
            this.uploadErrors = null;
            if (this.$refs.aiFile) {
                this.$refs.aiFile.value = '';
            }
        },
        async checkCredits(){
            try {
                this.availableCredits = await this.getAICredits();
            } catch (error) {
                this.availableCredits = null;
            }
        },
        onFileChange(event) {
            const files = Array.from(event.target.files);
            this.addFiles(files);
        },
        onDragOver() {
            this.isDragging = true;
        },
        onDragLeave() {
            this.isDragging = false;
        },
        onDrop(event) {
            this.isDragging = false;
            const files = Array.from(event.dataTransfer.files);
            this.addFiles(files);
        },
        isAllowed(file) {
            const validTypes = ['application/pdf', 'image/png', 'image/jpg', 'image/jpeg'];
            if (validTypes.includes(file.type)) return true;
            const name = (file.name || "").toLowerCase();
            return name.endsWith(".pdf") || name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg");
        },
        addFiles(files) {
            const validFiles = files.filter((file) => {
                if (!this.isAllowed(file)) {
                    this.setNotification({
                        msg: this.$t('importWithAi.notSupported', { name: file.name }),
                        type: "error",
                        color: "red",
                    });
                    return false;
                }
                return true;
            });
            if (this.multiple) {
                this.uploadedFiles = [...this.uploadedFiles, ...validFiles];
            } else {
                this.uploadedFiles = validFiles.slice(0, 1);
            }
            if (this.$refs.aiFile) {
                this.$refs.aiFile.value = '';
            }
        },
        removeFile(index) {
            this.uploadedFiles.splice(index, 1);
        },
        getFileType(fileName) {
            return fileName.split('.').pop().toUpperCase();
        },
        closeModal() {
            this.$bvModal.hide(this.modalId);
            this.resetFields();
        },
        async submitFiles() {
            if (this.uploadedFiles.length === 0) {
                this.uploadErrors = { document: [this.$t('importWithAi.pleaseSelectFile')] };
                return;
            }
            const credits = Number(this.availableCredits) || 0;
            if (credits <= 0) {
                this.uploadErrors = { document: [this.$t('importWithAi.noCredits')] };
                return;
            }
            if (this.uploadedFiles.length > credits) {
                await this.setNotification({
                    msg: this.$t('importWithAi.notEnoughCredits', {
                        credits,
                        files: this.uploadedFiles.length,
                        excess: this.uploadedFiles.length - credits,
                    }),
                    type: "error",
                    color: "red",
                });
                return;
            }
            this.uploadErrors = null;
            const isUpdate = !!this.maintenanceId;
            try {
                this.setLoader(true);
                const results = await Promise.allSettled(
                    this.uploadedFiles.map((file) => {
                        const formData = new FormData();
                        formData.append('file', file);
                        return isUpdate
                            ? this.updateMaintenanceFromDocument({ maintenanceId: this.maintenanceId, formData })
                            : this.extractMaintenanceFromDocument(formData);
                    })
                );
                const failed = results.filter((r) => r.status === 'rejected').length;
                const succeeded = results.length - failed;
                this.$emit("extractionQueued", results);
                await this.setNotification({
                    msg: failed === 0
                        ? (isUpdate ? this.$t('importWithAi.queuedUpdate', { count: succeeded }) : this.$t('importWithAi.queuedExtract', { count: succeeded }))
                        : this.$t('importWithAi.queuedMixed', { succeeded, failed }),
                    type: failed === 0 ? "success" : "error",
                    color: failed === 0 ? "green" : "red",
                });
                await this.getCompany()
                this.closeModal();
                this.setLoader(false);
            } catch (error) {
                this.setLoader(false);
                await this.setNotification({
                    msg: this.$t('importWithAi.uploadError'),
                    type: "error",
                    color: "red",
                });
            }
        },
    },
};
</script>

<style scoped>
.upload-btn-wrapper {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed #ccc;
    border-radius: 8px;
    padding: 20px;
    transition: all 0.3s ease;
}

.upload-btn-wrapper input[type=file] {
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
}

.files-list-container {
    width: 100%;
}

.files-list {
    max-height: 400px;
    overflow-y: auto;
}

.file-item {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.file-item:hover {
    background-color: #e9ecef;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.file-icon {
    display: flex;
    align-items: center;
    justify-content: center;
}

.file-info {
    flex-grow: 1;
    min-width: 0;
}

.file-info p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.font-12 {
    font-size: 12px;
}

.font-14 {
    font-size: 14px;
}

.h-240px {
    min-height: 240px;
}

.h-auto {
    min-height: auto;
}

.field-error {
    color: #dc3545;
    font-size: 12px;
    margin-top: 5px;
}

.ai-file-remove {
    position: static !important;
    cursor: pointer;
    transform: none !important;
}

.upload-btn-wrapper .img-2 {
    top: 40% !important;
}
</style>
